import { DomainsApiResponse, useDomainsQuery } from '@/hooks/api/use-domains-query'
import { parseAsString, useQueryState } from 'nuqs'
import React from 'react'

const MODERATION_DOMAIN_IDS = ['em-compliance']
const DATA_INTELLIGENCE_DOMAIN_ID = 'em-data-intelligence'
const isModerationDomainId = (domainId: string) => MODERATION_DOMAIN_IDS.includes(domainId)

/**
 * Gets the current domain from URL query parameters.
 * If no domain is set in the URL, it will set the first available domain as default.
 * If an invalid domain is set, it will redirect to the default domain.
 * If no domains are available, it will redirect to the settings page.
 */
function useDomainFromUrl() {
  const { data: domains = [], isLoading } = useDomainsQuery()
  const { domainId, setDomainId } = useDomainIdQueryState()

  const defaultDomainId = React.useMemo(() => getDefaultDomainId(domains), [domains])

  // Ensure domainId is always valid or set to default
  React.useEffect(() => {
    const isValidDomainId = domains.some((d) => d.externalId === domainId)
    if ((!domainId || !isValidDomainId) && defaultDomainId) {
      setDomainId(defaultDomainId)
    }
  }, [domainId, domains, defaultDomainId, setDomainId])

  return React.useMemo(() => {
    const isValid = domains.some((d) => d.externalId === domainId)
    const resolvedDomainId = !domainId || !isValid ? defaultDomainId : domainId

    return {
      domainId: resolvedDomainId,
      isDefaultDomain: !domainId || resolvedDomainId === defaultDomainId,
      domains,
      isLoading,
      isModerationDomain: isModerationDomainId(domainId),
    }
  }, [domains, domainId, isLoading, defaultDomainId])
}

function getDefaultDomainId(domains: DomainsApiResponse['data']) {
  return (
    domains.find((d) => d.name.includes('default') || d.externalId.includes('default'))
      ?.externalId ||
    domains.find((d) => d.name.includes('platform') || d.externalId.includes('platform'))
      ?.externalId ||
    domains[0]?.externalId ||
    ''
  )
}

const DOMAIN_ID_QUERY_KEY = 'domain'
const useDomainIdQueryState = () => {
  const [domainId, setDomainId] = useQueryState(
    DOMAIN_ID_QUERY_KEY,
    parseAsString.withDefault('').withOptions({ history: 'push' }),
  )
  return { domainId, setDomainId }
}

const DomainInitializer = ({ children }: React.PropsWithChildren) => {
  // Initialize domain from URL - this will set default domain if none is provided
  useDomainFromUrl()
  return children
}

export {
  DATA_INTELLIGENCE_DOMAIN_ID,
  DOMAIN_ID_QUERY_KEY,
  DomainInitializer,
  isModerationDomainId,
  useDomainFromUrl,
  useDomainIdQueryState,
}
